import { usePrivySmartWallet, useSwapContext } from "@src/contexts";
import { useMemo } from "react";
import { CASH_TOKEN_ADDRESS_BY_CHAIN } from "@src/config";
import { WalletActionsPanel } from "./WalletActionsPanel";
import { TotalValueCard } from "./TotalValueCard";
import { YourCashExpandable } from "./YourCashExpandable";
import { YourTokensVirtuoso } from "./YourTokensVirtuoso";
import { RoutePath } from "@src/interfaces";
import { BackArrowIcon } from "@src/assets";
import { Navigation, PageLayout } from "@src/components";
import { ControlsDropdownMenu } from "@src/features";

export const WalletView = () => {
  const { allTokens, tokensBalances, isFetchingBalances, tokensPrices } =
    useSwapContext();
  const { smartWalletAddress } = usePrivySmartWallet();

  const rawCash = useMemo(
    () =>
      allTokens.filter((t) => {
        const cashAddr = CASH_TOKEN_ADDRESS_BY_CHAIN[t.chainId].toLowerCase();
        const tAddr = t.address.toLowerCase();
        const balObj = tokensBalances[t.chainId]?.[tAddr];
        return cashAddr && tAddr === cashAddr && !!balObj && balObj.wei !== "0";
      }),
    [allTokens, tokensBalances],
  );

  const rawTokens = useMemo(
    () =>
      allTokens.filter((t) => {
        const tAddr = t.address.toLowerCase();
        const isCash =
          tAddr === CASH_TOKEN_ADDRESS_BY_CHAIN[t.chainId].toLowerCase();
        const balObj = tokensBalances[t.chainId]?.[tAddr];
        return !isCash && !!balObj && balObj.wei !== "0";
      }),
    [allTokens, tokensBalances],
  );

  const cashWithValue = useMemo(() => {
    return rawCash
      .map((t) => {
        const tAddr = t.address.toLowerCase();
        const balances = tokensBalances[t.chainId]?.[tAddr];
        if (!balances) return null;
        const balance = parseFloat(balances.humanReadable);
        const price = 1;
        return {
          ...t,
          price,
          value: balance * price,
          balances,
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null)
      .sort((a, b) => b.value - a.value);
  }, [rawCash, tokensBalances]);

  const tokensWithValue = useMemo(() => {
    return rawTokens
      .map((t) => {
        const tAddr = t.address.toLowerCase();
        const balances = tokensBalances[t.chainId]?.[tAddr];
        if (!balances) return null;
        const balance = parseFloat(balances.humanReadable);
        const price = parseFloat(tokensPrices[t.chainId]?.[tAddr] || "0");
        return {
          ...t,
          price,
          value: balance * price,
          balances,
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null)
      .sort((a, b) => b.value - a.value);
  }, [rawTokens, tokensBalances, tokensPrices]);

  const isLoading = useMemo(
    () => isFetchingBalances || !smartWalletAddress,
    [isFetchingBalances, smartWalletAddress],
  );

  return (
    <PageLayout
      navigation={
        <Navigation
          leftItems={[
            {
              content: <BackArrowIcon />,
              path: RoutePath.Buy,
            },
            {
              content: "Your wallet",
              disabled: true,
              path: RoutePath.Wallet,
            },
          ]}
          rightItems={[
            { content: <ControlsDropdownMenu />, isCustomContent: true },
          ]}
        />
      }
    >
      <div className="flex flex-col gap-2 h-full overflow-hidden">
        <TotalValueCard
          yourTokens={tokensWithValue}
          yourCash={cashWithValue}
          isLoading={isLoading}
        />

        <WalletActionsPanel />

        <YourCashExpandable yourCash={cashWithValue} isLoading={isLoading} />

        <YourTokensVirtuoso
          yourTokens={tokensWithValue}
          isLoading={isLoading}
        />
      </div>
    </PageLayout>
  );
};
